import onlineOrderingService from "../services/onlineOrderingService";
import { isOutsideOpeningHoursNow } from "./collectionTimeSlots";

/**
 * Map cart/UI order types to check-availability API orderType.
 * Returns null for browse / dine-in (no Delivery/Collection schedule gate).
 */
export function normalizeAvailabilityOrderType(orderType) {
  const t = String(orderType || "").trim().toLowerCase();
  if (t === "delivery") return "delivery";
  if (t === "collection" || t === "pickup") return "collection";
  return null;
}

export function orderTypeHoursLabel(orderType) {
  const apiType = normalizeAvailabilityOrderType(orderType);
  if (apiType === "delivery") return "Delivery";
  if (apiType === "collection") return "Collection";
  return "Ordering";
}

/** Back office stores schedules as "HH:mm"; guests read "5:00 PM". */
export function formatHoursTimeLabel(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const alreadyMeridiem = raw.match(/^(\d{1,2}):(\d{2})\s*([AaPp])\.?[Mm]\.?$/);
  if (alreadyMeridiem) {
    const [, hour, minute, meridiem] = alreadyMeridiem;
    return `${Number(hour)}:${minute} ${meridiem.toUpperCase()}M`;
  }

  const clock = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!clock) return raw;

  const hours = Number(clock[1]);
  if (!Number.isFinite(hours) || hours < 0 || hours > 24) return raw;
  const suffix = hours >= 12 && hours < 24 ? "PM" : "AM";
  const display = hours % 12 === 0 ? 12 : hours % 12;
  return `${display}:${clock[2]} ${suffix}`;
}

/** A window saved as the full day carries no useful information for guests. */
export function isAllDayHoursWindow(openingTime, closingTime) {
  const open = String(openingTime ?? "").trim();
  const close = String(closingTime ?? "").trim();
  return open === "00:00" && (close === "23:59" || close === "24:00" || close === "00:00");
}

/** "5:00 PM – 11:00 PM" for a day's configured window, or "" when the day is off. */
export function formatHoursRangeLabel(openingTime, closingTime) {
  const open = formatHoursTimeLabel(openingTime);
  const close = formatHoursTimeLabel(closingTime);
  if (!open || !close) return "";
  return `${open} – ${close}`;
}

export function formatOrderTypeHoursClosedMessage(orderType, apiMessage = "", openingTime = "", closingTime = "") {
  const label = orderTypeHoursLabel(orderType);
  // Prefer our own wording when today's window is known: the API phrases it in 24-hour time.
  const range = formatHoursRangeLabel(openingTime, closingTime);
  if (range) {
    return `${label} is not available right now. Hours today: ${range}.`;
  }
  const fromApi = (apiMessage || "").trim();
  if (fromApi) return fromApi;
  return `${label} is closed at this time. Please try again during ${label.toLowerCase()} hours.`;
}

/**
 * Guest-facing copy for a channel that can't take orders, split into a headline and a detail
 * line so the panel can lay them out. `reason` distinguishes a channel switched off in the back
 * office from one that is simply outside the hours configured for today.
 */
export function describeOrderTypeClosure(orderType, { reason = "hours", openingTime = "", closingTime = "" } = {}) {
  const label = orderTypeHoursLabel(orderType);

  if (reason === "channel") {
    return {
      title: `${label} isn’t available right now`,
      detail: "You can still browse the menu, and order once it reopens.",
    };
  }

  const range = formatHoursRangeLabel(openingTime, closingTime);
  if (range) {
    return {
      title: `${label} is closed at the moment`,
      detail: `Today’s ${label.toLowerCase()} hours are ${range}.`,
    };
  }

  return {
    title: `${label} is closed today`,
    detail: "Have a look at the menu and come back when we reopen.",
  };
}

/**
 * Live check against company Delivery/Collection schedules (server uses company local time).
 * @returns {Promise<{ ok: boolean, message: string, openingTime?: string, closingTime?: string }>}
 */
export async function assertOrderTypeHoursAvailable(restaurantSlug, orderType, locationId = null) {
  const apiType = normalizeAvailabilityOrderType(orderType);
  if (!restaurantSlug || !apiType) {
    return { ok: true, message: "" };
  }

  const availability = await onlineOrderingService.checkRestaurantAvailability(
    restaurantSlug,
    apiType,
    locationId ?? null
  );

  if (availability?.isAvailable) {
    return {
      ok: true,
      message: "",
      openingTime: availability.openingTime || "",
      closingTime: availability.closingTime || "",
    };
  }

  return {
    ok: false,
    message: formatOrderTypeHoursClosedMessage(
      apiType,
      availability?.message,
      availability?.openingTime,
      availability?.closingTime
    ),
    openingTime: availability?.openingTime || "",
    closingTime: availability?.closingTime || "",
  };
}

/** Prefer type-specific weekly hours string from restaurant payload. */
export function resolveHoursStringForOrderType(orderType, restaurantData, fallbackOpeningHours = "") {
  const apiType = normalizeAvailabilityOrderType(orderType);
  if (apiType === "delivery") {
    return (
      restaurantData?.deliveryOpeningHours ||
      restaurantData?.DeliveryOpeningHours ||
      fallbackOpeningHours ||
      restaurantData?.openingHours ||
      ""
    );
  }
  if (apiType === "collection") {
    return (
      restaurantData?.collectionOpeningHours ||
      restaurantData?.CollectionOpeningHours ||
      fallbackOpeningHours ||
      restaurantData?.openingHours ||
      ""
    );
  }
  return fallbackOpeningHours || restaurantData?.openingHours || "";
}

/**
 * Same closed check as the basket alert (weekly hours string).
 * Returns a guest-facing message when ordering should be blocked, or null when OK.
 */
export function getOrderingClosedMessage({
  orderType,
  restaurantData,
  restaurantDisplayName = "",
  fallbackOpeningHours = "",
} = {}) {
  const hours = resolveHoursStringForOrderType(
    orderType,
    restaurantData,
    fallbackOpeningHours || restaurantData?.openingHours || ""
  );
  if (!hours || !isOutsideOpeningHoursNow(hours)) {
    return null;
  }

  const apiType = normalizeAvailabilityOrderType(orderType);
  if (apiType === "delivery") {
    return "Delivery is closed right now. Try again during opening hours, or change order type.";
  }
  if (apiType === "collection") {
    return "Collection is closed right now. Try again during opening hours, or change order type.";
  }

  const name = String(
    restaurantDisplayName ||
      restaurantData?.companyName ||
      restaurantData?.CompanyName ||
      restaurantData?.name ||
      restaurantData?.restaurantName ||
      ""
  ).trim();

  return name
    ? `${name} is closed! Try again during opening hours.`
    : "This restaurant is closed! Try again during opening hours.";
}
